import React from "react";
import styled, { css } from "styled-components";

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: var(--space-2) 0;
`;

const Label = styled.label<{ $error?: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: ${(p) =>
    p.$error ? "var(--color-danger)" : "var(--color-text-secondary)"};
`;

const Shell = styled.div<{ $error?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: 40px;
  padding: 0 var(--space-2);
  background: ${(p) =>
    p.$disabled ? "var(--color-surface-subtle)" : "var(--color-surface)"};
  border: 1px solid
    ${(p) => (p.$error ? "var(--color-danger)" : "var(--color-border)")};
  border-radius: var(--radius-md);
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard),
    background var(--duration-fast) var(--ease-standard);

  ${(p) =>
    p.$disabled &&
    css`
      opacity: 0.7;
      cursor: not-allowed;
    `}

  &:focus-within {
    border-color: ${(p) =>
      p.$error ? "var(--color-danger)" : "var(--color-accent)"};
    box-shadow: var(--shadow-focus);
  }
`;

const Swatch = styled.input`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  &::-webkit-color-swatch {
    border: none;
    border-radius: var(--radius-sm);
  }
  &::-moz-color-swatch {
    border: none;
    border-radius: var(--radius-sm);
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const HexField = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-mono);
  font-size: var(--font-size-base);
  color: var(--color-text);
  text-transform: uppercase;

  &::placeholder {
    color: var(--color-text-tertiary);
    text-transform: none;
  }
  &:disabled {
    cursor: not-allowed;
    color: var(--color-text-tertiary);
  }
`;

const ErrorText = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-danger);
`;

const HEX_PATTERN = /^#([0-9a-fA-F]{6})$/;

const isFullHex = (value?: string): boolean =>
  typeof value === "string" && HEX_PATTERN.test(value.trim());

type ColorInputProps = {
  label?: string;
  error?: string;
  value?: string;
  onChange?: any;
  onBlur?: any;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  id?: string;
  placeholder?: string;
};

const ColorInput = ({
  label,
  error,
  value,
  onChange,
  onBlur,
  disabled,
  readOnly,
  name,
  id,
  placeholder = "#000000",
}: ColorInputProps): JSX.Element => {
  // The native swatch needs a valid 6-digit hex. Fall back to black so it
  // stays usable while the hex field is empty or mid-edit.
  const swatchValue = isFullHex(value) ? (value as string) : "#000000";

  return (
    <Wrapper>
      {label && (
        <Label htmlFor={id} $error={Boolean(error)}>
          {label}
        </Label>
      )}
      <Shell $error={Boolean(error)} $disabled={Boolean(disabled)}>
        <Swatch
          type="color"
          name={name}
          disabled={disabled || readOnly}
          value={swatchValue}
          onChange={onChange}
          aria-label={label ? `${label} color picker` : "Color picker"}
        />
        <HexField
          type="text"
          id={id}
          name={name}
          disabled={disabled}
          readOnly={readOnly}
          value={value ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
        />
      </Shell>
      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  );
};

export default ColorInput;
